/// <reference path="../../types/storyblock-globals.d.ts" />
import { getSessionData } from "@repo/shared-utils/common-utils";
import { useEffect, useRef, useState } from "react";
// tracker responsibilities moved to ./newRelicHtml5Tracker
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { createFireworkEventHandler } from "../newRelicTracking";
import { initHtml5Tracker, cleanupHtml5Tracker } from "./newRelicHtml5Tracker";
import { NEWRELIC_HTML5_VIDEO_CONFIG } from "../../constants/firework";
import { registerStoryblockListeners, cleanupStoryblockListeners } from "./eventListenerManager";
import { createStoryblockHandlers } from "./storyBlockEventHandlers";
import { videoDescriptionMap, videoMetaMap, FW_PRODUCT_CLICK_EVENTS } from "./storyBlockUtils";
import type { StoryBlockProps } from "../../components/StoryBlock/StoryBlock";
import { getStoryBlockViewModel } from "./storyBlockView";

export function useStoryBlockRuntime(props: Readonly<StoryBlockProps>) {
  const {
    origin,
    channel = "bajaj_finserv",
    playlist = props.origin === "live" ? "vVOVee" : undefined,
    videoId,
    autoplay = true,
    handleProductCardClick = "external",
    pipMode = "soft",
    playerPlacement = "bottom-left",
    openIn = "default",
    playerAddToCalendar = false,
    title = "Story-Block",
    titleCt,
    className = "",
    height = props.origin === "live" ? "715px" : "684px",
    mobileHeight = "504px",
    posterUrl,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const storyBlockRef = useRef<HTMLDivElement>(null);
  const fireworkNodeRef = useRef<HTMLElement | null>(null);
  const pipZIndexCleanupRef = useRef<(() => void) | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customerId, setCustomerId] = useState<string>("NA");
  const [blockHeight, setBlockHeight] = useState(height);
  const isMobile = useMediaQuery(768);
  const lastVideoIdRef = useRef<string | null>(null);
  const checkElementIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentObserverRef = useRef<MutationObserver | null>(null);

  // NewRelic HTML5 video tracker refs
  const videoTrackerRef = useRef<any>(null);
  const lastTrackedVideoElRef = useRef<HTMLVideoElement | null>(null);
  const trackerInitSignatureRef = useRef<string>("");
  const currentVideoTitleRef = useRef<string>("Unknown");
  const currentVideoIdRef = useRef<string>(videoId || "NA");
  const currentLiveStreamStatusRef = useRef<string>("idle");
  const nrLog = (..._args: unknown[]) => {};
  const nrError = (...args: unknown[]) => console.error("[NR HTML5 Agent]", ...args);

  // Firework comprehensive NewRelic event tracking handler
  const trackFireworkEvent = createFireworkEventHandler("StoryBlock", {
    sectionTitle: title,
  });

  const setStoryblockScopeZIndexForPip = (enabled: boolean): void => {
    if (typeof document === "undefined") return;
    const fireworkNode = fireworkNodeRef.current;
    if (!fireworkNode) return;

    const scopeEl = fireworkNode.closest(".storyblock-scope") as HTMLElement | null;
    if (!scopeEl) return;

    if (!enabled) {
      pipZIndexCleanupRef.current?.();
      pipZIndexCleanupRef.current = null;
      return;
    }

    if (pipZIndexCleanupRef.current) return;

    const previousInlineZIndex = scopeEl.style.zIndex;
    scopeEl.style.zIndex = "1";
    pipZIndexCleanupRef.current = () => {
      scopeEl.style.zIndex = previousInlineZIndex;
    };
  };

  const clearDetectionTimers = () => {
    if (checkElementIntervalRef.current) {
      clearInterval(checkElementIntervalRef.current);
      checkElementIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (contentTimeoutRef.current) {
      clearTimeout(contentTimeoutRef.current);
      contentTimeoutRef.current = null;
    }
    if (contentObserverRef.current) {
      contentObserverRef.current.disconnect();
      contentObserverRef.current = null;
    }
  };

  const cleanupFireworkNodeHandlers = (node: HTMLElement | null) => {
    cleanupStoryblockListeners(node);
  };

  const cleanupVideoTrackerState = () => {
    cleanupHtml5Tracker(
      {
        videoTrackerRef,
        lastTrackedVideoElRef,
        trackerInitSignatureRef,
        currentVideoIdRef,
        currentLiveStreamStatusRef,
      },
      { log: nrLog, error: nrError }
    );
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.__fwVideoDescriptionMap) {
      Object.assign(videoDescriptionMap, window.__fwVideoDescriptionMap);
    }
    if (typeof window !== "undefined" && window.__fwVideoMetaMap) {
      Object.assign(videoMetaMap, window.__fwVideoMetaMap);
    }

    const apiMeta = videoId ? videoMetaMap[videoId] : undefined;
    if (apiMeta?.contentTitle) {
      currentVideoTitleRef.current = apiMeta.contentTitle;
    }
    if (apiMeta?.videoId) {
      currentVideoIdRef.current = apiMeta.videoId;
    }
    if (apiMeta?.liveStreamStatus) {
      currentLiveStreamStatusRef.current = apiMeta.liveStreamStatus;
    }

    let isMounted = true;

    getSessionData()
      .then((session) => {
        if (!isMounted) return;
        setCustomerId(session?.custId || "NA");
      })
      .catch(() => {
        if (!isMounted) return;
        setCustomerId("NA");
      });

    return () => {
      isMounted = false;
    };
  }, [videoId]);

  useEffect(() => {
    return () => {
      setStoryblockScopeZIndexForPip(false);
    };
  }, []);

  useEffect(() => {
    if (isMobile && mobileHeight) {
      setBlockHeight(mobileHeight);
    } else {
      setBlockHeight(height);
    }
  }, [isMobile, height, mobileHeight]);

  useEffect(() => {
    setupFireworkEmbed();

    return () => {
      clearDetectionTimers();
      cleanupFireworkNodeHandlers(fireworkNodeRef.current);
      cleanupVideoTrackerState();
    };
  }, [
    channel,
    playlist,
    videoId,
    autoplay,
    handleProductCardClick,
    pipMode,
    playerPlacement,
    openIn,
    playerAddToCalendar,
    title,
    origin,
  ]);

  const setupFireworkEmbed = () => {
    // intentionally minimal logging here
    if (checkElementIntervalRef.current) {
      clearInterval(checkElementIntervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (contentTimeoutRef.current) {
      clearTimeout(contentTimeoutRef.current);
      contentTimeoutRef.current = null;
    }
    if (contentObserverRef.current) {
      contentObserverRef.current.disconnect();
      contentObserverRef.current = null;
    }

    const findScopedFireworkNode = (): HTMLElement | null => {
      const scopedNode = storyBlockRef.current?.querySelector<HTMLElement>("fw-storyblock") ?? null;
      if (scopedNode) return scopedNode;

      if (videoId) {
        const byVideoId = document.querySelector<HTMLElement>(
          `fw-storyblock[videoid="${videoId}"]`
        );
        if (byVideoId) return byVideoId;
      }

      if (playlist) {
        const byPlaylist = document.querySelector<HTMLElement>(
          `fw-storyblock[playlist="${playlist}"]`
        );
        if (byPlaylist) return byPlaylist;
      }

      return document.querySelector<HTMLElement>("fw-storyblock");
    };

    checkElementIntervalRef.current = setInterval(() => {
      const fireworkNode = findScopedFireworkNode();

      if (fireworkNode) {
        if (checkElementIntervalRef.current) {
          clearInterval(checkElementIntervalRef.current);
          checkElementIntervalRef.current = null;
        }

        fireworkNodeRef.current = fireworkNode;
        addEventListeners(fireworkNode);
        if (origin === "influencers") {
          beginContentReadyDetection(fireworkNode);
        }
      }
    }, 100);
    // Removed 10s timeout: event listeners register whenever fw-storyblock appears,
    // without any time limit. Interval continues until component unmounts.
  };

  const initVideoTracker = (fireworkElement: HTMLElement, retries = 5): void => {
    nrLog("initVideoTracker called", { retries });
    if (typeof window === "undefined") {
      nrLog("initVideoTracker: no window available");
      return;
    }
    if (!window.newrelic) {
      nrLog("initVideoTracker: window.newrelic not present yet");
      if (retries > 0) {
        setTimeout(() => initVideoTracker(fireworkElement, retries - 1), 500);
      }
      return;
    }

    nrLog("initVideoTracker: calling initHtml5Tracker", { fireworkElement });
    const initialized = initHtml5Tracker(
      fireworkElement,
      {
        videoTrackerRef,
        lastTrackedVideoElRef,
        trackerInitSignatureRef,
        currentVideoTitleRef,
        currentVideoIdRef,
        currentLiveStreamStatusRef,
      },
      { log: nrLog, error: nrError },
      videoId,
      NEWRELIC_HTML5_VIDEO_CONFIG
    );

    nrLog("initVideoTracker: initHtml5Tracker returned", { initialized });

    if (!initialized) {
      if (retries > 0) {
        nrLog("initVideoTracker: will retry", { retriesLeft: retries - 1 });
        setTimeout(() => initVideoTracker(fireworkElement, retries - 1), 500);
      } else {
        nrError("Html5Tracker initialization failed: video element not found or init error");
      }
    }
  };

  const beginContentReadyDetection = (fireworkNode: HTMLElement) => {
    if (fireworkNode.childNodes.length > 0) {
      setIsLoading(false);
      return;
    }

    contentObserverRef.current = new MutationObserver(() => {
      if (fireworkNode.childNodes.length > 0) {
        setIsLoading(false);
        contentObserverRef.current?.disconnect();
        contentObserverRef.current = null;
        if (contentTimeoutRef.current) {
          clearTimeout(contentTimeoutRef.current);
          contentTimeoutRef.current = null;
        }
      }
    });

    contentObserverRef.current.observe(fireworkNode, {
      childList: true,
      subtree: true,
    });

    contentTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      if (contentObserverRef.current) {
        contentObserverRef.current.disconnect();
        contentObserverRef.current = null;
      }
      contentTimeoutRef.current = null;
    }, 3000);
  };

  const addEventListeners = (fireworkNode: HTMLElement): void => {
    const handlers = createStoryblockHandlers({
      origin,
      title,
      titleCt,
      getCustomerId: () => customerId,
      videoId,
      containerRef,
      setIsLoading,
      fireworkNodeRef,
      initVideoTracker: (el: HTMLElement) => initVideoTracker(el),
      lastVideoIdRef,
      currentVideoTitleRef,
      currentVideoIdRef,
      currentLiveStreamStatusRef,
      trackFireworkEvent: (ev: string) => trackFireworkEvent(ev),
      setStoryblockScopeZIndexForPip,
    });

    registerStoryblockListeners(fireworkNode, handlers, Array.from(FW_PRODUCT_CLICK_EVENTS));
  };

  const view = getStoryBlockViewModel({
    origin,
    className,
    isLoading,
    isMobile,
    blockHeight,
  });

  const storyblockAttrs = {
    channel,
    playlist,
    videoid: videoId,
    autoplay: autoplay ? "true" : "false",
    handle_product_card_click: handleProductCardClick,
    pip_mode: pipMode,
    qr_code: "false",
    player_placement: playerPlacement,
    open_in: openIn,
    player_add_to_calendar: playerAddToCalendar ? "true" : "false",
    livestream_product_card: "fullscreen",
    player_close: "true",
    ui_share_sheet: "copy",
    share_template: "https://www.bajajfinserv.in/live?fw_video=vVqJmb&fw_channel=bajaj_finserv",
    dock: "false",
    email_embed: "false",
    height: blockHeight,
    ...(origin === "influencers"
      ? {
          max_videos: "1",
          role: "application",
          "aria-live": "polite",
          name: `player-${videoId || playlist}`,
          tabIndex: 0,
          pip: "true",
        }
      : {}),
  } as Record<string, unknown>;

  return {
    containerRef,
    storyBlockRef,
    fireworkNodeRef,
    isLoading,
    isMobile,
    blockHeight,
    ...view,
    posterUrl,
    storyblockAttrs,
  };
}
